/**
 * Set-name aliases between our catalog and a pricing vendor's.
 *
 * Our set names come from TCGdex, which follows Cardmarket nomenclature.
 * Poketrace's US catalog follows TCGplayer nomenclature. On promo sets the two
 * do not line up at all: what we call "MEP Black Star Promos" is filed by
 * Poketrace as "ME: Mega Evolution Promo", and the matcher rejects it as a
 * different set. One row in pricing_set_aliases repairs an entire set.
 *
 * This is the tier-2 fix target for the pricing repair loop. Rows are written
 * by the diagnosis function after it has verified the mapping actually resolves
 * the card, so anything in here has already been proven against the live API.
 */

import { supabase } from '../lib/supabase';

// One fetch per session. Aliases change on the order of days, not seconds, and
// the matcher runs on every card open.
let cache = null;
let inflight = null;

function keyOf(setName, vendor) {
  return `${(setName || '').trim().toLowerCase()}::${vendor}`;
}

// The leading letters of a card number, which is how the vendor's subset
// splits are recognised: TG11 belongs to the Trainer Gallery, 011/172 does not.
function numberPrefix(cardNumber) {
  const m = String(cardNumber || '').trim().match(/^([A-Za-z]+)/);
  return m ? m[1].toUpperCase() : '';
}

async function load() {
  if (cache) return cache;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await supabase
        .from('pricing_set_aliases')
        .select('our_set_name, vendor, vendor_set_name, search_hint, market, number_prefix')
        .eq('active', true);

      if (error) throw error;

      // Several aliases can share a set name, differing by number prefix.
      const map = new Map();
      for (const row of data || []) {
        const k = keyOf(row.our_set_name, row.vendor);
        const list = map.get(k) || [];
        list.push(row);
        map.set(k, list);
      }
      cache = map;
      return cache;
    } catch (err) {
      console.warn('[PricingAliases] load failed, continuing without aliases:', err.message);
      // An empty map, not null: a failed alias fetch must never block pricing.
      cache = new Map();
      return cache;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Resolve our set name to the vendor's, if a verified mapping exists.
 *
 * @param {string} setName - our set name, as stored on the card
 * @param {string} [cardNumber] - needed to pick between whole-set and subset rows
 * @param {string} [vendor='poketrace']
 * @returns {Promise<{vendorSetName: string, searchHint: string|null, market: string|null}|null>}
 */
export async function resolveSetAlias(setName, cardNumber, vendor = 'poketrace') {
  if (!setName) return null;
  const map = await load();
  const rows = map.get(keyOf(setName, vendor));
  if (!rows?.length) return null;

  // A prefixed alias is the more specific claim and wins. The unprefixed row is
  // the fallback for everything else in the set.
  const prefix = numberPrefix(cardNumber);
  const row = rows.find((r) => r.number_prefix && r.number_prefix.toUpperCase() === prefix)
    || rows.find((r) => !r.number_prefix)
    || null;
  // A set with only a subset alias must not fall through to it. Brilliant Stars
  // base cards have no TG prefix and belong to the base set, not the gallery.
  if (!row) return null;

  return {
    vendorSetName: row.vendor_set_name,
    searchHint: row.search_hint || null,
    market: row.market || null,
  };
}

/** Drop the cached aliases so the next lookup refetches. Used after a fix lands. */
export function invalidateAliasCache() {
  cache = null;
}

export default { resolveSetAlias, invalidateAliasCache };
